/**
 * ViewState — capture/apply the subset of viewer state that a Noach "view"
 * (widok) persists, layered onto an ALREADY-initialized scene.
 *
 * Unlike SaveProject/LoadProject this:
 *   - captures only measurements (distance/angle/point/height/area),
 *     surfaces (seed + params; occupancy is rebuilt on load),
 *     volume clipping boxes, annotations, the camera view, and a small
 *     settings subset — NOT pointclouds, profiles, camera animations,
 *     oriented images, or geopackages (the "unused tools" the issue drops);
 *   - never (re)loads point clouds — the host (PotreeViewer) loads them with
 *     the authoritative cloud_alignments poses, and content visibility/material
 *     is handled by the host under the JSON `noach` block;
 *   - round-trips measurement color (LoadProject.loadMeasurement drops it).
 *
 * Exposed as Potree.captureViewState / Potree.applyViewState.
 */

import * as THREE from '../../libs/three.js/build/three.module.js';
import { Annotation } from '../Annotation.js';
import { Measure } from '../utils/Measure.js';
import {
  Surface,
  normalizeSurfaceVis,
  MAX_SURFACES_PER_VIEW,
  clampExtraPlasterM,
  clampProfileOffsetM,
  clampProfileRangeM,
  normalizeProfileRef,
  normalizeVolumeTop,
  normalizeProfileBands,
} from '../utils/Surface.js';
import { BoxVolume, SphereVolume } from '../utils/Volume.js';
import { ClipTask, ClipMethod, ElevationGradientRepeat } from '../defines.js';

const VOLUME_CTORS = { BoxVolume, SphereVolume };

function enumKey(enumObj, value) {
  const entry = Object.entries(enumObj).find((e) => e[1] === value);
  return entry ? entry[0] : undefined;
}

// ===== capture =====

function captureView(viewer) {
  const view = viewer.scene.view;
  return {
    position: view.position.toArray(),
    target: view.getPivot().toArray(),
  };
}

function captureSettings(viewer) {
  // Background is intentionally omitted — it is theme-driven by the host.
  // pointBudget is intentionally omitted too: it is a machine/performance
  // knob, not view content. Persisting it lets a low value captured on one
  // session override the host's configured budget on every later open, which
  // starves the LOD loader (endless octree.bin load/evict churn when the
  // visible set exceeds the budget's load limit).
  return {
    fov: viewer.getFOV(),
    // edlEnabled is a machine/display preference (default ON). Persisting it
    // in a shared view lets an old capture taken while EDL defaulted to off
    // disable shading for everyone who opens that view.
    edlRadius: viewer.getEDLRadius(),
    edlStrength: viewer.getEDLStrength(),
    minNodeSize: viewer.getMinNodeSize(),
    showBoundingBoxes: viewer.getShowBoundingBox(),
    clipTask: enumKey(ClipTask, viewer.getClipTask()),
    clipMethod: enumKey(ClipMethod, viewer.getClipMethod()),
    // "Gradient repeat" for the elevation ramp. Viewer-level like clipTask —
    // viewer.update() pushes it onto every visible cloud's material each frame
    // — so it belongs here and NOT in the host's per-cloud material block,
    // where the next frame would overwrite it. NONE also gates
    // uElevationClipEnabled, so this changes which points render.
    elevationGradientRepeat: enumKey(
      ElevationGradientRepeat,
      viewer.elevationGradientRepeat,
    ),
  };
}

function normScanKey(key) {
  return key == null || key === '' ? null : String(key);
}

function findSurfacePointcloud(viewer, cloudUuid, scanKey) {
  if (!cloudUuid) return null;
  const wantScan = normScanKey(scanKey);
  for (const pc of viewer.scene?.pointclouds || []) {
    if (pc.noachCloudUuid !== cloudUuid) continue;
    if (normScanKey(pc.noachScanKey) === wantScan) return pc;
  }
  return null;
}

function capturePlane(s) {
  if (!s?.hasPlane && !s?.detected) return undefined;
  const n = s.plane?.normal;
  const o = s.plane?.origin;
  if (!Array.isArray(n) || n.length < 3 || !n.slice(0, 3).every(Number.isFinite)) return undefined;
  const plane = { normal: [n[0], n[1], n[2]] };
  if (Array.isArray(o) && o.length >= 3 && o.slice(0, 3).every(Number.isFinite)) {
    plane.origin = [o[0], o[1], o[2]];
  }
  return plane;
}

function captureSurface(s) {
  if (!s?.cloudUuid || !s.seedOffset) return null;
  const seed = typeof s.seedOffset.toArray === 'function'
    ? s.seedOffset.toArray()
    : [s.seedOffset.x, s.seedOffset.y, s.seedOffset.z];
  if (!Array.isArray(seed) || seed.length < 3 || !seed.slice(0, 3).every(Number.isFinite)) {
    return null;
  }
  return {
    uuid: s.uuid,
    name: s.name,
    color: s.color?.toArray?.() || [1, 0, 0],
    visible: s.visible !== false,
    folder: s.folder ?? null,
    kind: 'surface',
    visMode: normalizeSurfaceVis(s),
    cloudUuid: s.cloudUuid,
    scanKey: normScanKey(s.scanKey),
    seed: [seed[0], seed[1], seed[2]],
    plane: capturePlane(s),
    angleDeg: s.angleDeg,
    maxDistance: s.maxDistance,
    maxGap: s.maxGap,
    extraPlasterM: s.extraPlasterM,
    // Height-profile scale; a null range = follow the grown distance.
    profileOffsetM: s.profileOffsetM,
    profileRangeBelowM: s.profileRangeBelowM,
    profileRangeAboveM: s.profileRangeAboveM,
    profileRangeLinked: s.profileRangeLinked !== false,
    profileRef: s.profileRef,
    volumeTop: s.volumeTop,
    profileBands: s.profileBands,
    showHighlightExtremes: !!s.showHighlightExtremes,
    flipped: !!s.flipped,
  };
}

function captureMeasurement(m) {
  return {
    uuid: m.uuid,
    name: m.name,
    points: m.points.map((p) => p.position.toArray()),
    showDistances: m.showDistances,
    showCoordinates: m.showCoordinates,
    showArea: m.showArea,
    closed: m.closed,
    showAngles: m.showAngles,
    showHeight: m.showHeight,
    showEdges: m.showEdges,
    // showCircle / showAzimuth intentionally dropped (unused tools)
    color: m.color.toArray(),
    folder: m.folder,
  };
}

function captureVolume(v) {
  return {
    uuid: v.uuid,
    type: v.type || v.constructor.name,
    name: v.name,
    position: v.position.toArray(),
    rotation: v.rotation.toArray(),
    scale: v.scale.toArray(),
    visible: v.visible,
    clip: v.clip,
    folder: v.folder,
  };
}

function captureAnnotation(a) {
  const data = {
    uuid: a.uuid,
    title: a.title != null ? a.title.toString() : '',
    description: a.description,
    position: a.position.toArray(),
    offset: a.offset.toArray(),
    folder: a.folder,
    children: a.children.map(captureAnnotation),
  };
  const nonZero = (v) => v && !(v.x === 0 && v.y === 0 && v.z === 0);
  if (nonZero(a.cameraPosition)) {
    data.cameraPosition = {
      x: a.cameraPosition.x,
      y: a.cameraPosition.y,
      z: a.cameraPosition.z,
    };
  }
  if (nonZero(a.cameraTarget)) {
    data.cameraTarget = {
      x: a.cameraTarget.x,
      y: a.cameraTarget.y,
      z: a.cameraTarget.z,
    };
  }
  if (typeof a.radius !== 'undefined') {
    data.radius = a.radius;
  }
  return data;
}

export function captureViewState(viewer) {
  const scene = viewer.scene;
  return {
    view: captureView(viewer),
    settings: captureSettings(viewer),
    measurements: scene.measurements
      .filter((m) => !(m instanceof BoxVolume || m instanceof SphereVolume))
      .filter((m) => m.kind !== 'surface')
      .map(captureMeasurement),
    volumes: scene.volumes
      .filter((v) => v instanceof BoxVolume || v instanceof SphereVolume)
      .map(captureVolume),
    annotations: scene.annotations.children.map(captureAnnotation),
    surfaces: (scene.surfaces || [])
      .slice(0, MAX_SURFACES_PER_VIEW)
      .map(captureSurface)
      .filter(Boolean),
  };
}

// ===== apply =====

function applySettings(viewer, data) {
  if (!data) return;
  // data.pointBudget deliberately IGNORED (even in states saved before it was
  // dropped from capture) — see captureSettings.
  if (data.fov != null) viewer.setFOV(data.fov);
  // data.edlEnabled deliberately IGNORED (even in states saved before it was
  // dropped from capture) — see captureSettings.
  if (data.edlRadius != null) viewer.setEDLRadius(data.edlRadius);
  if (data.edlStrength != null) viewer.setEDLStrength(data.edlStrength);
  if (data.minNodeSize != null) viewer.setMinNodeSize(data.minNodeSize);
  if (data.showBoundingBoxes != null) {
    viewer.setShowBoundingBox(data.showBoundingBoxes);
  }
  if (data.clipTask && ClipTask[data.clipTask] != null) {
    viewer.setClipTask(ClipTask[data.clipTask]);
  }
  if (data.clipMethod && ClipMethod[data.clipMethod] != null) {
    viewer.setClipMethod(ClipMethod[data.clipMethod]);
  }
  if (
    data.elevationGradientRepeat &&
    ElevationGradientRepeat[data.elevationGradientRepeat] != null
  ) {
    viewer.setElevationGradientRepeat(
      ElevationGradientRepeat[data.elevationGradientRepeat],
    );
  }
}

export function applyViewCamera(viewer, view) {
  if (!view || !view.position || !view.target) return;
  viewer.scene.view.setView([...view.position], [...view.target]);
}

function applySurface(viewer, data) {
  if (!data?.uuid || !data.cloudUuid || !Array.isArray(data.seed) || data.seed.length < 3) {
    return;
  }
  if ((viewer.scene.surfaces?.length ?? 0) >= MAX_SURFACES_PER_VIEW) return;
  if (viewer.scene.surfaces?.find((s) => s.uuid === data.uuid)) return;

  const seed = new THREE.Vector3(
    Number(data.seed[0]),
    Number(data.seed[1]),
    Number(data.seed[2]),
  );
  if (![seed.x, seed.y, seed.z].every(Number.isFinite)) return;

  const color = new THREE.Color();
  if (Array.isArray(data.color) && data.color.length >= 3) color.fromArray(data.color);

  const surface = new Surface({
    pointcloud: findSurfacePointcloud(viewer, data.cloudUuid, data.scanKey),
    cloudUuid: data.cloudUuid,
    scanKey: normScanKey(data.scanKey),
    seedOffset: seed,
    color,
    name: data.name,
  });
  surface.uuid = data.uuid;
  surface.folder = data.folder ?? null;
  surface.visMode = normalizeSurfaceVis(data);
  if (Number.isFinite(data.angleDeg)) surface.angleDeg = data.angleDeg;
  if (Number.isFinite(data.maxDistance)) surface.maxDistance = data.maxDistance;
  if (Number.isFinite(data.maxGap)) surface.maxGap = data.maxGap;
  if (Number.isFinite(data.extraPlasterM)) surface.extraPlasterM = clampExtraPlasterM(data.extraPlasterM);
  surface.profileOffsetM = clampProfileOffsetM(data.profileOffsetM);
  // Views saved before the split carried one symmetric `profileRangeM`; it seeds both sides.
  surface.profileRangeBelowM = clampProfileRangeM(data.profileRangeBelowM ?? data.profileRangeM);
  surface.profileRangeAboveM = clampProfileRangeM(data.profileRangeAboveM ?? data.profileRangeM);
  // Linked unless the view says otherwise; a linked pair always agrees (below wins).
  surface.profileRangeLinked = data.profileRangeLinked !== false;
  if (surface.profileRangeLinked) surface.profileRangeAboveM = surface.profileRangeBelowM;
  // Older views have no key → fitted, i.e. exactly today's colouring.
  surface.profileRef = normalizeProfileRef(data.profileRef);
  // Older views have no key → highest highlighted point, i.e. exactly today's volume.
  surface.volumeTop = normalizeVolumeTop(data.volumeTop);
  // Older views have no key → 0 (continuous), i.e. exactly today's colouring.
  surface.profileBands = normalizeProfileBands(data.profileBands);
  // Older views have no key → hidden, i.e. exactly today's overlay.
  // `showVolumeExtent` was the key for a few days before the markers moved to
  // the highlighted extremes; honour it so those views keep their toggle.
  surface.showHighlightExtremes = (data.showHighlightExtremes ?? data.showVolumeExtent) === true;
  surface.grownAngleDeg = surface.angleDeg;
  surface.grownMaxDistance = surface.maxDistance;
  surface.flipped = !!data.flipped;
  if (data.plane?.normal && Array.isArray(data.plane.normal) && data.plane.normal.length >= 3) {
    const n = data.plane.normal.map(Number);
    const o = Array.isArray(data.plane.origin) ? data.plane.origin.map(Number) : [0, 0, 0];
    if (n.every(Number.isFinite)) {
      surface.plane = {
        origin: o.length >= 3 && o.every(Number.isFinite) ? [o[0], o[1], o[2]] : [0, 0, 0],
        normal: [n[0], n[1], n[2]],
      };
      surface.hasPlane = true;
      // Keep the packed snapshot in step with the restored plane: the constructor
      // aliases gpuPlane to the default [0,0,1] plane, so until the restore re-grow
      // packs, `gpuPlane || plane` readers (SurfacePanel's profile legend) would
      // otherwise report a level floor for a restored wall. The render path is
      // unaffected — occCount is 0, so this surface is never uploaded yet.
      surface.gpuPlane = surface.plane;
    }
  }
  if (data.visible === false) surface.setVisible(false);
  surface.busy = true;

  viewer.surfaceTool?.attach?.(surface);
  viewer.scene.addSurface(surface);
  if (surface.pointcloud) surface._updateOverlay(viewer);
}

/**
 * Attach restored surfaces to pointclouds that loaded after applyViewState,
 * then re-run grow so highlight / area / volume are rebuilt (occupancy is
 * never stored in the view).
 */
export function bindViewSurfaces(viewer) {
  const surfaces = viewer.scene?.surfaces || [];
  const tool = viewer.surfaceTool;
  for (const surface of surfaces) {
    tool?.attach?.(surface);
    if (!surface.pointcloud) {
      surface.pointcloud = findSurfacePointcloud(
        viewer,
        surface.cloudUuid,
        surface.scanKey,
      );
    }
    if (surface.pointcloud) surface._updateOverlay(viewer);
  }
}

export function growRestoredSurfaces(viewer) {
  const tool = viewer.surfaceTool;
  if (!tool?.grow) return Promise.resolve();
  const jobs = [];
  for (const surface of viewer.scene?.surfaces || []) {
    if (surface.detected || surface._restoreGrown) continue;
    if (!surface.pointcloud || !surface.cloudUuid) continue;
    // The host stamps `false` when the backend has no occupancy sidecar for
    // the cloud; a grow would 409 and toast on every pointcloud_added.
    // `undefined` (non-noach hosts) keeps the old behaviour. applySurface
    // left it busy pending this re-grow, so drop the spinner too.
    if (surface.pointcloud.noachSurfaceReady === false) {
      surface.busy = false;
      continue;
    }
    surface._restoreGrown = true;
    surface.busy = true;
    jobs.push(
      Promise.resolve(tool.grow(surface)).then(() => {
        if (!surface.detected) surface._restoreGrown = false;
      })
    );
  }
  return Promise.all(jobs);
}

function applyMeasurement(viewer, data) {
  if (!data || data.kind === 'surface') return;
  if (viewer.scene.measurements.find((m) => m.uuid === data.uuid)) return;

  const measure = new Measure();
  measure.uuid = data.uuid;
  measure.name = data.name;
  measure.showDistances = data.showDistances;
  measure.showCoordinates = data.showCoordinates;
  measure.showArea = data.showArea;
  measure.closed = data.closed;
  measure.showAngles = data.showAngles;
  measure.showHeight = data.showHeight;
  measure.showEdges = data.showEdges;
  measure.folder = data.folder;

  for (const point of data.points || []) {
    measure.addMarker(new THREE.Vector3(...point));
  }
  // Round-trip color (LoadProject.loadMeasurement leaves this as a TODO).
  if (Array.isArray(data.color) && measure.color && measure.color.fromArray) {
    measure.color.fromArray(data.color);
  }

  viewer.scene.addMeasurement(measure);
}

function applyVolume(viewer, data) {
  if (viewer.scene.volumes.find((v) => v.uuid === data.uuid)) return;

  const Ctor = VOLUME_CTORS[data.type] || BoxVolume;
  const volume = new Ctor();
  volume.uuid = data.uuid;
  volume.name = data.name;
  volume.position.set(...data.position);
  volume.rotation.set(...data.rotation);
  volume.scale.set(...data.scale);
  volume.visible = data.visible;
  volume.clip = data.clip;
  volume.folder = data.folder;

  viewer.scene.addVolume(volume);
}

function makeAnnotation(item) {
  let cameraPosition = null;
  let cameraTarget = null;
  if (item.cameraPosition && item.cameraTarget) {
    cameraPosition = new THREE.Vector3(
      item.cameraPosition.x,
      item.cameraPosition.y,
      item.cameraPosition.z,
    );
    cameraTarget = new THREE.Vector3(
      item.cameraTarget.x,
      item.cameraTarget.y,
      item.cameraTarget.z,
    );
  }

  const annotation = new Annotation({
    position: item.position,
    title: item.title,
    cameraPosition,
    cameraTarget,
  });
  annotation.description = item.description;
  annotation.uuid = item.uuid;
  annotation.folder = item.folder;
  if (item.offset) annotation.offset.set(...item.offset);
  return annotation;
}

function applyAnnotations(viewer, items) {
  if (!Array.isArray(items)) return;

  const exists = (uuid) => {
    let found = false;
    viewer.scene.annotations.traverse((a) => {
      if (a.uuid === uuid) found = true;
    });
    return found;
  };

  const build = (item, parent) => {
    if (exists(item.uuid)) return;
    const annotation = makeAnnotation(item);
    for (const child of item.children || []) build(child, annotation);
    parent.add(annotation);
  };

  for (const item of items) build(item, viewer.scene.annotations);
}

/**
 * Apply a captured view-state subset onto an already-initialized scene.
 * Idempotent per object (dedups by uuid). Camera is applied separately via
 * applyViewCamera so the host can choose when (e.g. after fitToScreen).
 */
export function applyViewState(viewer, state, options = {}) {
  if (!state) return;
  viewer.scene?.removeAllSurfaces?.();
  applySettings(viewer, state.settings);
  if (Array.isArray(state.measurements)) {
    for (const m of state.measurements) applyMeasurement(viewer, m);
  }
  if (Array.isArray(state.volumes)) {
    for (const v of state.volumes) applyVolume(viewer, v);
  }
  applyAnnotations(viewer, state.annotations);
  if (Array.isArray(state.surfaces)) {
    for (const s of state.surfaces.slice(0, MAX_SURFACES_PER_VIEW)) {
      applySurface(viewer, s);
    }
  }
  if (options.applyCamera !== false) {
    applyViewCamera(viewer, state.view);
  }
}
